var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, FlexBox, Icon, Text } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { articles } from 'utils/elevio';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useDelayedState } from 'utils/useDelayedState';
import { AnalyticsDocumentationButton } from '../documentation/analyticsDocumentationButton';
import styles from './analyticsSidenav.module.scss';
import { AnalyticsSidenavItem } from './components/item/analyticsSidenavItem';
import { AnalyticsSidenavSection } from './components/section/analyticsSidenavSection';
export const AnalyticsSidenav = ({ isExpandable = true, }) => {
    const dispatch = useAppDispatch();
    const [expanded, setExpanded] = useDelayedState(false, 100);
    const { pathname } = useLocation();
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const dashboards = analyticsApi.endpoints.readDashboards.useQueryState(undefined, {
        selectFromResult: ({ data }) => { var _a; return (_a = data === null || data === void 0 ? void 0 : data.data.dashboards) !== null && _a !== void 0 ? _a : []; },
    });
    const { quickViews, isLoading } = analyticsApi.useReadFavoriteQuickViewsQuery(undefined, {
        selectFromResult: (_a) => {
            var _b;
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { quickViews: (_b = data === null || data === void 0 ? void 0 : data.data.quick_views) !== null && _b !== void 0 ? _b : [] }));
        },
    });
    const isActivePath = useCallback((dashboard, quickView) => {
        if (quickView) {
            return (pathname ===
                `/analytics/dashboard/${dashboard.slug}/quick-view/${quickView.id}`);
        }
        return pathname === `/analytics/dashboard/${dashboard.slug}`;
    }, [pathname]);
    const dashboardsSection = useMemo(() => {
        const getLinkOptions = (dashboard) => ({
            path: '/analytics/dashboard/:slug',
            params: {
                path: {
                    slug: dashboard.slug,
                },
            },
        });
        return {
            title: 'Dashboards',
            items: dashboards
                .filter((dashboard) => !dashboard.parent_id)
                .map((dashboard) => {
                const subDashboards = dashboards.filter(({ parent_id }) => parent_id === dashboard.id);
                if (subDashboards.length) {
                    return {
                        type: 'accordion',
                        title: dashboard.title,
                        icon: dashboard.icon,
                        active: false,
                        options: {
                            subItems: [
                                {
                                    title: 'All',
                                    active: isActivePath(dashboard),
                                    options: getLinkOptions(dashboard),
                                },
                                ...subDashboards.map((subDashboard) => ({
                                    title: subDashboard.title,
                                    active: isActivePath(subDashboard),
                                    options: getLinkOptions(subDashboard),
                                })),
                            ],
                        },
                    };
                }
                return {
                    type: 'link',
                    title: dashboard.title,
                    icon: dashboard.icon,
                    active: isActivePath(dashboard),
                    options: getLinkOptions(dashboard),
                };
            }),
        };
    }, [dashboards, isActivePath]);
    const quickViewsSection = useMemo(() => {
        const getLinkOptions = (quickView) => {
            var _a;
            return ({
                path: '/analytics/dashboard/:slug/quick-view/:id',
                params: {
                    path: {
                        slug: (_a = dashboards.find((dashboard) => dashboard.id === quickView.dashboard_id)) === null || _a === void 0 ? void 0 : _a.slug,
                        id: quickView.id,
                    },
                },
            });
        };
        return {
            title: 'Quick views',
            items: [
                {
                    type: 'accordion',
                    title: 'Quick views',
                    icon: 'star',
                    active: false,
                    options: {
                        isLoading,
                        emptyState: (_jsxs(FlexBox, Object.assign({ flexDirection: "column", alignItems: "center", gap: "300" }, { children: [_jsxs(FlexBox, Object.assign({ flexDirection: "column", alignItems: "center", gap: "100" }, { children: [_jsx(Text.Paragraph, Object.assign({ variant: "3", subdued: true }, { children: "No favorites yet" })), _jsx(Text.Paragraph, Object.assign({ variant: "4", subdued: true }, { children: "You haven't favorited any quick views." }))] })), _jsx(AppLink, Object.assign({ path: "/analytics/quick-views" }, { children: _jsx(Button, Object.assign({ variant: "secondary" }, { children: "See all quick views" })) }))] }))),
                        subItems: quickViews.length
                            ? [
                                {
                                    title: 'All',
                                    active: pathname === '/analytics/quick-views',
                                    options: {
                                        path: '/analytics/quick-views',
                                        params: {},
                                    },
                                },
                                ...quickViews
                                    // Not needed when calling the real API
                                    .filter((quickView) => quickView.favorited)
                                    .map((quickView) => {
                                    const dashboard = dashboards.find(({ id }) => id === quickView.dashboard_id);
                                    return {
                                        title: quickView.name,
                                        active: dashboard
                                            ? isActivePath(dashboard, quickView)
                                            : false,
                                        options: getLinkOptions(quickView),
                                    };
                                }),
                            ]
                            : [],
                        action: (_jsx(AnalyticsDocumentationButton, { type: "article", articleId: articles.analytics.quickViews })),
                    },
                },
            ],
        };
    }, [dashboards, isActivePath, isLoading, pathname, quickViews]);
    const bottomSection = {
        title: 'More',
        items: [
            {
                type: 'button',
                title: 'Send feedback',
                icon: 'paper-plane',
                active: false,
                options: {
                    onClick: () => dispatch(appActions.showModal({
                        modal: {
                            type: 'app:feedbackForm',
                            arg: {
                                title: 'Analytics Feedback',
                                team: 'insights',
                            },
                        },
                    })),
                },
            },
        ],
    };
    const sections = [
        dashboardsSection,
        ...(quickViewsSection ? [quickViewsSection] : []),
        bottomSection,
    ];
    return (_jsx("nav", Object.assign({ "aria-label": "Analytics Sidenav", className: classNames(styles.sidenav, {
            [styles.sidenavExpanded]: expanded,
        }), style: {
            '--sidenav-width-collapsed': analyticsSidenavWidth,
        } }, (isExpandable
        ? {
            onMouseEnter: () => setExpanded(true),
            onMouseLeave: () => setExpanded(false),
        }
        : {}), { children: _jsxs(FlexBox, Object.assign({ flexDirection: "column", height: "100%" }, { children: [_jsx("div", Object.assign({ className: classNames(styles.expander, {
                        [styles.expanderVisible]: !expanded,
                    }) }, { children: isExpandable && (_jsx(Icon, { name: "angle-double-right", size: 24, "aria-label": "Expand" })) })), sections.map((section, sectionIndex) => {
                    const sectionKey = `section-${sectionIndex}`;
                    const isNotLast = sectionIndex < sections.length - 1;
                    return (_jsx(AnalyticsSidenavSection, Object.assign({ section: section, hidden: isSmallScreen ? !expanded : !expanded && isNotLast }, { children: section.items.map((item, itemIndex) => (_jsx(AnalyticsSidenavItem, { item: item }, `${sectionKey}-item-${itemIndex}`))) }), sectionKey));
                })] })) })));
};
export const analyticsSidenavWidth = '56px';
